import { useParams } from "@tanstack/react-router";
import React, { useState, useMemo } from "react";
import { useGateways } from "../context/GatewayContext";
import ContentFramework from "../components/ContentFramework";
import InfoRow from "../components/InfoRow";
import { toast } from "react-hot-toast";
import SrcfulMessageProtocolFactory from '../services/backend/SrcfulMessageProtocolFactory';
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import GatewayPageHeader from '../components/GatewayPageHeader';
import { handleSubmitFlow } from '../components/utils/gatewayUtils';
import WifiConfigForm from '../components/form/WifiConfigForm';
import PageContainer from "../components/layout/PageContainer";
import { useWalletContext } from '../context/WalletContext';
import { WalletTypeDetector } from "../wallets/WalletTypeDetector";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

interface ExpandableBoxProps {
  title: string;
  children: React.ReactNode;
}

const ExpandableBox: React.FC<ExpandableBoxProps> = ({ title, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="w-full max-w-3xl p-4 border border-[#292929] rounded-[8px] bg-[#1D1D1D]">
      <button
        className="w-full flex justify-between items-center"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h2 className="text-base font-bold leading-7">{title}</h2>
        {isExpanded ? (
          <ChevronUpIcon className="h-5 w-5" />
        ) : (
          <ChevronDownIcon className="h-5 w-5" />
        )}
      </button>
      {isExpanded && <div className="mt-4">{children}</div>}
    </div>
  );
};

const NetworkInterface = ({ title, ipAddress, connectedSSID }: { title: string; ipAddress: string | undefined; connectedSSID?: string }) => (
  <div>
    <h2 className="text-lg font-bold leading-7 mb-3">{title}</h2>
    <div className="divide-y divide-[#292929]">
      {title === "WiFi" && (
        <InfoRow
          label="Connected to"
          value={connectedSSID || "Not Connected"}
        />
      )}
      <InfoRow
        label="IP Address"
        value={ipAddress || "Not Connected"}
        isCopyable={Boolean(ipAddress)}
      />
    </div>
  </div>
);

const WifiConfigSection = ({
  selectedNetwork,
  setSelectedNetwork,
  password,
  setPassword
}: {
  selectedNetwork: string;
  setSelectedNetwork: (network: string) => void;
  password: string;
  setPassword: (password: string) => void;
}) => {
  const { publicKey, wallet } = useWalletContext();
  const { activeGateway } = useGateways();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  
  // Check if user is in guest mode
  const isGuestMode = useMemo(() => WalletTypeDetector.isGuestWallet(wallet), [wallet]);

  const availableSSIDs: string[] = activeGateway?.state?.network?.wifi?.ssids || [];

  const handleScan = async () => {
    if (!publicKey || !activeGateway || !wallet) return;
    setIsScanning(true);
    
    const message = SrcfulMessageProtocolFactory.createWifiScanMessage({
      walletPublicKey: publicKey.toString(),
      gatewayId: activeGateway.id
    });

    await handleSubmitFlow(message, wallet, () => {
      toast.success('WiFi scan initiated');
      setTimeout(() => {
        setIsScanning(false);
      }, 30000);
    }, () => {
      toast.error('Failed to initiate WiFi scan');
      setIsScanning(false);
    });
  };

  const handleSubmit = async (ssid: string, psk: string) => {
    if (!publicKey || !activeGateway || !wallet) return { success: false };

    setSelectedNetwork(ssid);
    setPassword(psk);
    setIsSubmitting(true);

    const message = SrcfulMessageProtocolFactory.createWifiConfigMessage({
      walletPublicKey: publicKey.toString(),
      gatewayId: activeGateway.id,
      ssid,
      psk
    });

    try {
      await handleSubmitFlow(message, wallet, () => {
        toast.success('WiFi configuration sent successfully');
      }, () => {
        toast.error('Failed to set WiFi configuration');
      });
      return { success: true };
    } catch (error) {
      return { success: false };
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-400 border border-[#292929] rounded-[8px] bg-[#1D1D1D] p-3 flex items-start">
        <span className="text-yellow-400 mr-2">⚠️</span>
        <span>Changing WiFi settings may disconnect your gateway. Make sure you have a backup connection method available.</span>
      </p>
      
      {isGuestMode ? (
        <div className="space-y-4">
          <div>
            <label htmlFor="ssid" className="block text-sm font-medium mb-1">
              SSID
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <select
                  id="ssid"
                  value=""
                  disabled={true}
                  className="w-full bg-[#1D1D1D] rounded-[8px] border border-[#292929] pl-4 pr-12 py-2 text-sm appearance-none opacity-50"
                >
                  <option value="">Select a network...</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                  <svg className="fill-current h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <button
                disabled={true}
                className="flex items-center justify-center space-x-2 px-3 py-2 rounded-[8px] bg-gray-500 text-gray-300 cursor-not-allowed opacity-50"
                title="Not available in Guest Mode"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                <span>Scan Wifi</span>
              </button>
            </div>
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type="password"
                value=""
                disabled={true}
                className="w-full bg-[#1D1D1D] rounded-[8px] border border-[#292929] px-3 py-2 pr-10 text-sm opacity-50"
                placeholder="Network password"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              disabled={true}
              className="flex items-center justify-center space-x-2 px-3 py-2 rounded-[8px] bg-gray-500 text-gray-300 cursor-not-allowed opacity-50"
              title="Not available in Guest Mode"
            >
              <span>Configure WiFi</span>
            </button>
          </div>
        </div>
      ) : (
        <WifiConfigForm
          availableSSIDs={availableSSIDs}
          onSubmit={handleSubmit}
          onScan={handleScan}
          isScanning={isScanning}
          isSubmitting={isSubmitting}
          initialSSID={selectedNetwork}
          initialPassword={password}
        />
      )}
    </div>
  );
};

interface GatewayNetworkProps {
  gateways: any[] | null;
  activeGateway: any;
  setActiveGateway: (gateway: any) => void;
  gatewayId?: string;
  isConnected: boolean;
}

class GatewayNetwork extends React.Component<GatewayNetworkProps> {
  state = {
    selectedNetwork: '',
    password: ''
  };

  componentDidUpdate(prevProps: GatewayNetworkProps) {
    const { gatewayId, gateways, setActiveGateway } = this.props;
    if (gatewayId && gateways && (gatewayId !== prevProps.gatewayId || gateways !== prevProps.gateways)) {
      const gateway = gateways.find(g => g.id === gatewayId);
      if (gateway) {
        setActiveGateway(gateway);
      }
    }
  }

  render() {
    const { isConnected, activeGateway } = this.props;
    const { selectedNetwork, password } = this.state;

    if (!isConnected || !activeGateway) return null;

    return (
      <ContentFramework>
        <PageContainer>
          <GatewayPageHeader 
            gatewayName={activeGateway.name}
          />
          <h1 className="text-4xl font-bold mb-6">Network Configuration</h1>
          
          {/* Network Interfaces */}
          <div className="w-full mb-8">
            <div className="divide-y divide-[#292929]">
              <div className="pb-6">
                <NetworkInterface 
                  title="Ethernet" 
                  ipAddress={activeGateway.state?.network?.address?.interfaces?.eth0} 
                />
              </div>
              <div className="py-6">
                <NetworkInterface 
                  title="WiFi" 
                  ipAddress={activeGateway.state?.network?.address?.interfaces?.wlan0}
                  connectedSSID={activeGateway.state?.network?.wifi?.connected}
                />
              </div>
            </div>
          </div>

          {/* WiFi Configuration */}
          <div>
            <ExpandableBox title="WiFi Configuration">
              <WifiConfigSection 
                selectedNetwork={selectedNetwork}
                setSelectedNetwork={(network: string) => this.setState({ selectedNetwork: network })}
                password={password}
                setPassword={(pwd: string) => this.setState({ password: pwd })}
              />
            </ExpandableBox>
          </div>
        </PageContainer>
      </ContentFramework>
    );
  }
}

const GatewayNetworkWrapper = () => {
  const { isConnected } = useWalletContext();
  const { gateways, activeGateway, setActiveGateway } = useGateways();
  const { gatewayId } = useParams({ from: '/gateway/$gatewayId/network' });

  return (
    <GatewayNetwork
      isConnected={isConnected}
      gateways={gateways}
      activeGateway={activeGateway}
      setActiveGateway={setActiveGateway}
      gatewayId={gatewayId}
    />
  );
};

export default GatewayNetworkWrapper; 